import styled, { DefaultTheme } from 'styled-components';
import {
  Card,
  Button,
  Input,
  InputNumber,
  Space,
  Typography,
  Divider,
  Modal,
  Image,
} from 'antd';
import { CheckOutlined } from '@ant-design/icons';

// Declare module augmentation for styled-components theme
declare module 'styled-components' {
  export interface DefaultTheme {
    mode?: 'light' | 'dark';
  }
}

// Theme helper function
const getThemeValue = <T,>(props: { theme?: DefaultTheme }, darkValue: T, lightValue: T): T => {
  return props.theme?.mode === 'dark' ? darkValue : lightValue;
};

export const Container = styled.div`
  width: 100%;
  max-width: 100vw;
  overflow-x: hidden;
`;

export const HeaderCard = styled(Card)`
  border-radius: ${(props) => (props.theme?.mode === 'dark' ? '12px' : '16px')};
  border: 1px solid ${(props) => getThemeValue(props, '#434343', '#e8e8e8')};
  box-shadow: ${(props) => getThemeValue(props, '0 2px 8px rgba(0, 0, 0, 0.3)', '0 2px 8px rgba(0, 0, 0, 0.08)')};
  background: ${(props) => getThemeValue(props, '#1a202c', '#ffffff')};
  margin-bottom: 24px;

  .ant-card-body {
    border-radius: 12px;
    padding: ${(props) => (props.theme?.mode === 'dark' ? '24px' : '32px')};
    background: ${(props) => getThemeValue(props, '#1a202c', '#ffffff')};

    @media (max-width: 768px) {
      padding: 16px;
    }
  }
`;

// Title
export const Title = styled(Typography.Title)`
  margin-bottom: 8px !important;
  color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')} !important;
  font-weight: 700 !important;
  letter-spacing: -0.02em !important;

  @media (max-width: 768px) {
    font-size: 20px !important;
    text-align: center;
  }
`;

export const Subtitle = styled(Typography.Text)`
  font-size: 14px;
  color: #718096 !important;

  @media (max-width: 768px) {
    font-size: 12px;
    text-align: center;
    display: block;
  }
`;

// Controls
export const ControlsRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 24px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
  }
`;

export const LastUpdatedText = styled(Typography.Text)`
  font-size: 12px;
  color: #718096 !important;
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const RefreshButton = styled(Button)`
  border: none;
  background: transparent;
  color: ${(props) => getThemeValue(props, '#fff', '#595959')};
  
  &:hover {
    background: ${(props) => getThemeValue(props, '#4a5568', '#f5f5f5')};
    color: ${(props) => getThemeValue(props, '#fff', '#262626')};
  }
`;

export const CreateButton = styled(Button)`
  background: #2D6CCA !important;
  border-color: #2D6CCA !important;
  color: #fff !important;
  height: 38px;
  border-radius: 8px;
  font-weight: 500;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;

  &:hover {
    background: ${(props) => getThemeValue(props, '#1a4a8f', '#4A8FE0')} !important;
    border-color: ${(props) => getThemeValue(props, '#1a4a8f', '#4A8FE0')} !important;
    color: #fff !important;
  }

  @media (max-width: 768px) {
    width: 100%;
    min-width: 200px;
  }
`;

// Search Bar
export const SearchCard = styled(Card)`
  border-radius: 12px;
  border: 1px solid ${(props) => getThemeValue(props, '#434343', '#e8e8e8')};
  background: ${(props) => getThemeValue(props, '#1a202c', '#ffffff')};
  margin-bottom: 24px;

  .ant-card-body {
    padding: 16px;
    background: ${(props) => getThemeValue(props, '#1a202c', '#ffffff')};
    border-radius: 12px;
  }
`;

export const StyledSearchInput = styled(Input)`
  border-radius: 999px !important;
  height: 40px;
  background: transparent !important;

  .ant-input {
    background: transparent !important;
    border: none !important;
    color: ${(props) => getThemeValue(props, '#fff', '#262626')} !important;

    &::placeholder {
      color: ${(props) => getThemeValue(props, '#8c8c8c', '#8c8c8c')} !important;
    }
  }

  &:hover,
  &:focus {
    .ant-input {
      background: transparent !important;
      border: none !important;
    }
  }
`;

// Create Form
export const CreateFormCard = styled(Card)`
  border-radius: 16px;
  border: 1px solid ${(props) => getThemeValue(props, '#434343', '#e8e8e8')};
  box-shadow: ${(props) => getThemeValue(props, '0 2px 8px rgba(0, 0, 0, 0.3)', '0 2px 8px rgba(0, 0, 0, 0.08)')};
  background: ${(props) => getThemeValue(props, '#1a202c', '#ffffff')};
  margin-bottom: 24px;

  .ant-card-body {
    padding: 24px;
    background: ${(props) => getThemeValue(props, '#1a202c', '#ffffff')};
    border-radius: 12px;
  }
`;

export const FormItemLabel = styled(Typography.Text)`
  font-weight: 500;
  margin-bottom: 8px;
  display: block;
  color: ${(props) => getThemeValue(props, '#fff', '#262626')} !important;
`;

export const HelperText = styled(Typography.Text)`
  font-size: 12px;
  color: ${(props) => getThemeValue(props, '#8c8c8c', '#8c8c8c')} !important;
  display: block;
  margin-bottom: 8px;
`;

export const StyledInput = styled(Input)`
  border-radius: 8px;
  height: 38px;
  margin-top: 8px;
`;

export const StyledNumberInput = styled(Input)`
  border-radius: 8px;
  height: 38px;
`;

export const FormActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;

    button {
      width: 100%;
    }
  }
`;

// Customization List
export const ListContainer = styled.div`
  display: flex;
  gap: 16px;
  width: 100%;
  flex-direction: column;

  @media (min-width: 992px) {
    flex-direction: row;
  }
`;

export const CustomizationList = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const CustomizationCard = styled(Card)<{ isSelected?: boolean }>`
  border-radius: 12px;
  border: 1px solid ${(props) => getThemeValue(props, '#434343', '#e8e8e8')};
  background: ${(props) => getThemeValue(props, '#1a202c', '#ffffff')};
  cursor: grab;
  transition: all 0.3s ease-in-out;
  
  &:active {
    cursor: grabbing;
  }
  box-shadow: ${(props) => getThemeValue(props, '0 2px 8px rgba(0, 0, 0, 0.3)', '0 2px 8px rgba(0, 0, 0, 0.08)')};

  &:hover {
    background: ${(props) => getThemeValue(props, '#2d3748', '#fafafa')};
    transform: translateY(-2px);
    box-shadow: ${(props) => getThemeValue(props, '0 4px 12px rgba(0, 0, 0, 0.4)', '0 4px 12px rgba(0, 0, 0, 0.12)')};
    
    .ant-card-body {
      background: ${(props) => getThemeValue(props, '#2d3748', '#f0f0f0')};
    }
  }

  .ant-card-body {
    padding: 16px;
    border-radius: 12px;
    background: ${(props) => getThemeValue(props, '#1a202c', '#ffffff')};
    transition: background 0.3s ease-in-out;

    &:hover {
      background: ${(props) => getThemeValue(props, '#2d3748', '#f0f0f0')};
    }
  }
`;

export const CustomizationHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  flex-wrap: wrap;
  gap: 12px;
`;

export const CustomizationInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

export const CustomizationTitle = styled(Typography.Title)`
  margin: 0 !important;
  color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')} !important;
  font-size: 16px !important;
  font-weight: 600 !important;

  @media (max-width: 768px) {
    font-size: 14px !important;
  }
`;

export const CustomizationMeta = styled(Typography.Text)`
  font-size: 12px;
  color: #718096 !important;
`;

export const ActionButtons = styled(Space)`
  flex-wrap: wrap;
`;

export const IconButton = styled(Button)`
  border: none;
  background: transparent;
  padding: 4px 8px;
  height: auto;

  &:hover {
    background: ${(props) => getThemeValue(props, '#4a5568', '#f5f5f5')};
  }
`;

export const DuplicateButton = styled(Button)`
  height: 32px;
  min-width: 32px;
  font-size: 14px;
  padding: 0 12px;
  color: #38A169 !important;
  border-color: currentColor !important;
  background: transparent !important;
  border-radius: 15px !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  box-shadow: none !important;
  font-weight: 600 !important;
  
  &:hover {
    color: #38A169 !important;
    border-color: #68D391 !important;
    background: #F0FFF4 !important;
    box-shadow: none !important;
  }
  
  @media (max-width: 768px) {
    display: none;
  }
`;

export const DuplicateIconButton = styled(Button)`
  @media (min-width: 769px) {
    display: none;
  }
`;

// Expanded Content
export const ExpandedContent = styled.div`
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid ${(props) => getThemeValue(props, '#434343', '#e8e8e8')};
  background: transparent;
`;

export const OptionItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  flex-wrap: wrap;
  gap: 8px;
`;

// export const OptionNameText = styled(Typography.Text)`
//   font-size: 14px;
//   color: ${(props) => getThemeValue(props, '#fff', '#262626')} !important;

//   @media (max-width: 768px) {
//     font-size: 12px;
//   }
// `;

export const SoldOutButtonSmall = styled(Button)`
  background: transparent !important;
  border: 1.5px solid #ff4d4f !important;
  color: #ff4d4f !important;
  height: 28px;
  padding: 0 10px;
  font-size: 12px;
  border-radius: 6px;
  font-weight: 500;
  box-shadow: none !important;

  &:hover {
    background: rgba(255, 77, 79, 0.08) !important;
    border-color: #ff4d4f !important;
    color: #ff4d4f !important;
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

export const RelatedItemsSection = styled.div`
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid ${(props) => getThemeValue(props, '#434343', '#e8e8e8')};
`;

export const RelatedItemsTitle = styled(Typography.Text)`
  font-size: 12px;
  font-weight: 600;
  color: ${(props) => getThemeValue(props, '#14b8a6', '#14b8a6')} !important;
  display: block;
  margin-bottom: 8px;
`;

export const RelatedItemText = styled(Typography.Text)`
  font-size: 11px;
  color: #718096 !important;
  display: block;
  margin: 4px 0;
`;

// Side Panel
export const SidePanel = styled.div`
  width: 100%;
  background: ${(props) => getThemeValue(props, '#1a202c', '#ffffff')};
  border-radius: 12px;
  border: 1px solid ${(props) => getThemeValue(props, '#434343', '#e8e8e8')};
  padding: 16px;
  min-height: 500px;
  max-height: calc(100vh - 40px);
  overflow-y: auto;

  @media (min-width: 992px) {
    width: 400px;
    position: sticky;
    top: 20px;
  }

  @media (max-width: 991px) {
    order: -1;
    margin-bottom: 24px;
  }
`;

// Loading and Empty States
export const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
`;

export const EmptyContainer = styled.div`
  text-align: center;
  padding: 48px 16px;
  color: ${(props) => getThemeValue(props, '#bfbfbf', '#8c8c8c')};
`;

export const PaginationText = styled(Typography.Text)`
  text-align: center;
  display: block;
  padding: 16px;
  color: #718096 !important;
`;

// Action Icon Button (same style as RoleManagement)
export const ActionIconButton = styled.div<{ iconColor?: string }>`
  align-items: center;
  border: 1px solid ${(props) => getThemeValue(props, '#434343', '#eee')};
  border-radius: 10px;
  display: flex;
  height: 30px;
  justify-content: center;
  padding: 8px;
  width: 30px;
  cursor: pointer;
  transition: background-color 0.2s, color 0.2s;
  background: ${(props) => getThemeValue(props, 'transparent', 'inherit')};

  svg {
    transition: color 0.2s;
  }

  &:hover {
    background-color: ${(props) => {
      const iconColor = props.iconColor || '#2D6CCA';
      if (iconColor === '#f5222d') {
        return getThemeValue(props, 'rgba(245, 34, 45, 0.2)', 'rgba(245, 34, 45, 0.1)');
      }
      return getThemeValue(props, 'rgba(45, 108, 202, 0.2)', 'rgba(45, 108, 202, 0.1)');
    }} !important;

    svg {
      color: ${(props) => props.iconColor || '#2D6CCA'} !important;
    }
  }
`;

// Common Styled Card (used in AddCustomisations, AddToItems, CustomizationRules)
export const StyledCard = styled(Card)<{ $width?: string; $isFixed?: boolean }>`
  display: flex;
  flex-direction: column;
  width: ${(props) => props.$width || '25%'};
  position: ${(props) => (props.$isFixed ? 'fixed' : 'relative')};
  right: ${(props) => (props.$isFixed ? '16px' : 'auto')};
  height: calc(100vh - 120px);
  max-width: inherit;
  border-left: ${(props) => (props.$isFixed ? '1px solid #d9d9d9' : 'none')};
  overflow: hidden;

  .ant-card-body {
    padding: 0;
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    overflow: hidden;
  }
`;

// Common Styled Button (used in AddCustomisations, AddToItems, CustomizationRules)
export const StyledButton = styled(Button)`
  background-color: #2D6CCA !important;
  color: white !important;
  
  &:hover {
    background-color: #1e4d8f !important;
    color: white !important;
  }
  
  &:focus {
    background-color: #2D6CCA !important;
    color: white !important;
  }
  
  &:active {
    background-color: #2D6CCA !important;
    color: white !important;
  }
  
  width: 100%;
  text-align: left;
  height: auto;
  padding: 9px 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border: none;
  margin-top: auto;
`;

// Styled Button for CustomizationRules (with margin-top)
export const StyledSaveButton = styled(Button)`
  width: 100%;
  margin-top: 24px;
  background: black;
  color: white;
  border: none;
  
  &:hover {
    background: white !important;
    color: black !important;
    border: 1px solid #d9d9d9 !important;
  }
`;


// Item Card for AddToItems
export const ItemCard = styled.div<{ $isDark?: boolean }>`
  padding: 12px;
  width: 100%;
  border-radius: 8px;
  border: 1px solid ${(props) => (props.$isDark ? '#434343' : '#e8e8e8')};
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: ${(props) => (props.$isDark ? '#1a202c' : '#fafafa')};
  margin-bottom: 8px;
  cursor: pointer;
  
  &:hover {
    background: ${(props) => (props.$isDark ? '#2d3748' : '#f0f0f0')};
  }
`;

// Scrollable Container for AddToItems
export const ScrollableContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  margin-bottom: 16px;
  max-height: calc(100vh - 450px);
`;

// Loading Spinner Container
export const LoadingSpinnerContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px;
`;

// Empty State Container
export const EmptyStateContainer = styled.div`
  text-align: center;
  padding: 40px;
`;

// Additional styled components for removing inline styles
export const FullWidthSpace = styled(Space)`
  width: 100%;
  flex: 1;
  display: flex;
`;

export const FullWidthSpaceVertical = styled(Space)`
  width: 100%;
`;

export const FullWidthSpaceSmall = styled(Space)`
  width: 100%;
`;

export const FullWidthSpaceLarge = styled(Space)`
  width: 100%;
`;

export const FlexSpace = styled(Space)`
  width: 100%;
  justify-content: space-between;
`;

export const CursorPointerText = styled(Typography.Text)`
  cursor: pointer;
`;

export const CursorPointerIcon = styled.div`
  cursor: pointer;
  font-size: 18px;
`;

export const TitleNoMargin = styled(Typography.Title)`
  margin: 0 !important;
`;

export const FlexContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 8px;
`;

export const FlexContainerCenter = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
`;

export const FlexContainerCenterPadding = styled.div`
  display: flex;
  justify-content: center;
  padding: 16px;
`;

export const FlexItem = styled.div`
  flex: 1;
`;

export const StyledDivider = styled(Divider)`
  margin: 16px 0;
`;

export const SecondaryTextSmall = styled(Typography.Text)`
  font-size: 13px;
  display: block;
  line-height: 1.5;
  color: ${({ theme }) => theme?.mode === 'dark' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.55)'} !important;
`;

export const SecondaryTextSmallPadding = styled(Typography.Text)`
  font-size: 12px;
  padding-left: 16px;
`;

export const TextFontSize = styled(Typography.Text)`
  font-size: 14px;
  margin-bottom: 8px;
`;

export const StyledButtonNoBorder = styled(Button)`
  border: none;
`;

export const StyledButtonIcon = styled(Button)`
  border-radius: 50%;
  width: auto;
  padding: 4px 8px;
`;

export const SearchIcon = styled.div`
  color: #8c8c8c;
`;

export const InputMarginBottom = styled(Input)`
  margin-bottom: 16px;
`;

export const StyledImage = styled(Image)`
  border-radius: 8px;
`;

export const HelperTextContainer = styled.div`
  margin-top: 4px;
  margin-bottom: 10px;
`;

export const InputNumberFullWidth = styled(InputNumber)`
  width: 100%;
`;

export const InputNumberFullWidthMargin = styled(InputNumber)`
  width: 100%;
  margin-top: 8px;
`;

export const StyledModal = styled(Modal)`
  .ant-modal {
    top: 20px;
    max-height: 90vh;
  }

  .ant-modal-content {
    display: flex;
    flex-direction: column;
  }

  .ant-modal-body {
    overflow-y: auto;
  }
`;

export const StickyContainer = styled.div`
  position: sticky;
  top: 20px;
  max-height: calc(100vh - 160px);
  overflow-y: auto;
  width: 100%;
  padding: 16px;
  padding-top: 0;
  margin-left: 16px;
  background-color: ${(props) => getThemeValue(props, '#1a202c', '#ffffff')};
  border-radius: 8px;
`;

// EditOptions styled components
export const OptionCard = styled.div<{ $isDark?: boolean }>`
  padding: 12px;
  width: 100%;
  border-radius: 8px;
  border: 1px solid ${(props) => getThemeValue(props, '#434343', '#e8e8e8')};
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: ${(props) => getThemeValue(props, '#1a202c', '#f5f5f5')};
  margin-bottom: 12px;
  cursor: grab;

  &:hover {
    background: ${(props) => getThemeValue(props, '#232b38', '#ebebeb')};
  }

  &:active {
    cursor: grabbing;
  }
`;

export const OptionInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const OptionName = styled(Typography.Text)`
  font-weight: 600;
  font-size: 14px;
  color: ${(props) => getThemeValue(props, '#fff', '#262626')} !important;
  margin: 0;
`;

export const OptionPrice = styled(Typography.Text)`
  font-size: 13px;
  font-weight: 500;
  color: ${(props) => getThemeValue(props, '#9ca3af', '#718096')} !important;
  margin: 0;
`;

export const OptionActions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
`;

export const SoldOutButton = styled(Button)`
  background: transparent !important;
  border: 1.5px solid #ff4d4f !important;
  color: #ff4d4f !important;
  height: 28px;
  padding: 0 10px;
  font-size: 12px;
  border-radius: 6px;
  font-weight: 500;
  box-shadow: none !important;

  &:hover {
    background: rgba(255, 77, 79, 0.08) !important;
    border-color: #ff4d4f !important;
    color: #ff4d4f !important;
  }
`;

export const OptionButtonsContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

export const CreateOptionButton = styled(Button)`
  width: 100%;
  height: 40px;
  border-radius: 8px;
  border: 1px solid ${(props) => getThemeValue(props, '#434343', '#d9d9d9')};
  background: ${(props) => getThemeValue(props, '#2d3748', '#ffffff')};
  color: ${(props) => getThemeValue(props, '#fff', '#262626')};
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 16px;
  
  &:hover {
    background: ${(props) => getThemeValue(props, '#4a5568', '#fafafa')};
    border-color: ${(props) => getThemeValue(props, '#434343', '#d9d9d9')};
  }
`;

export const EditOptionsSaveButton = styled(Button)`
  background: black !important;
  color: white !important;
  border: none !important;
  height: 40px;
  border-radius: 8px;
  font-weight: 500;
  margin-top: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  
  &:hover {
    background: white !important;
    color: black !important;
  }
`;

export const EditOptionsContainer = styled.div<{ $width?: string; $isFixed?: boolean }>`
  display: flex;
  flex-direction: column;
  width: ${(props) => props.$width || '25%'};
  position: ${(props) => (props.$isFixed ? 'fixed' : 'relative')};
  right: ${(props) => (props.$isFixed ? '16px' : 'auto')};
  height: calc(100vh - 120px);
  max-width: inherit;
  border-left: ${(props) => (props.$isFixed ? '1px solid #d9d9d9' : 'none')};
  overflow: hidden;
`;

export const OptionsListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 16px;
`;

// Suggestions box for EditOptions
export const SuggestionsContainer = styled.div`
  position: relative;
`;

export const SuggestionsBox = styled.div<{ $isDark?: boolean }>`
  position: absolute;
  background: ${(props) => getThemeValue(props, '#1a202c', '#ffffff')};
  border: 1px solid ${(props) => getThemeValue(props, '#434343', '#e8e8e8')};
  border-radius: 8px;
  margin-top: 8px;
  width: 100%;
  z-index: 1000;
  max-height: 150px;
  overflow-y: auto;
`;

export const SuggestionItem = styled.div<{ $isDark?: boolean; $isSelected?: boolean }>`
  padding: 8px 12px;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: ${(props) => getThemeValue(props, '#1a202c', '#ffffff')};
  
  &:hover {
    background: ${(props) => getThemeValue(props, '#2d3748', '#f5f5f5')};
  }
`;

export const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 16px;
`;

export const FormItemContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const FormLabel = styled(Typography.Text)`
  font-weight: 500;
  color: ${(props) => getThemeValue(props, '#fff', '#262626')} !important;
`;

export const StickyButtonContainer = styled.div`
  margin-top: 16px;
  position: sticky;
  bottom: 0;
  width: 100%;
`;

// Additional styled components for EditOptions
export const BreadcrumbSpace = styled(Space)`
  margin-bottom: 16px;
`;

export const TitleWithMargin = styled(TitleNoMargin)`
  margin-bottom: 16px !important;
`;

export const EditOptionsLoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 16px;
  min-height: 200px;
`;

export const SaveButtonAutoWidth = styled(EditOptionsSaveButton)`
  width: auto;
  margin-left: auto;
  background-color: #2D6CCA !important;
  border-color: #2D6CCA !important;
  color: white !important;
  transform: none !important;
  
  &:hover {
    background-color: #1e4d8f !important;
    color: white !important;
    transform: none !important;
  }
  
  &:focus {
    background-color: #2D6CCA !important;
    color: white !important;
    transform: none !important;
  }
  
  &:active {
    background-color: #2D6CCA !important;
    color: white !important;
    transform: none !important;
  }
`;

export const BreadcrumbSpaceWrap = styled(Space)`
  margin-bottom: 16px;
  
  &.ant-space {
    flex-wrap: wrap;
  }
`;

export const OptionPreviewContainer = styled.div`
  margin-top: 16px;
  padding: 12px;
  background: #f5f5f5;
  border-radius: 8px;
`;

export const OptionPreviewPrice = styled(Typography.Text)`
  font-size: 12px;
`;

export const FullWidthInputNumber = styled(InputNumber)`
  width: 100%;
`;

export const OptionCardDraggable = styled(OptionCard)<{ $isDraggable?: boolean }>`
  opacity: ${(props) => (props.$isDraggable ? 1 : 0.8)};
  cursor: ${(props) => (props.$isDraggable ? 'grab' : 'default')};
`;

export const CheckIconGreen = styled(CheckOutlined)`
  color: #52c41a;
`;

export const CreateButtonPrimary = styled(Button)`
  background-color: #2D6CCA !important;
  color: white !important;
  
  &:hover {
    background-color: #1e4d8f !important;
    border-color: #1e4d8f !important;
    color: white !important;
  }
  
  &:focus {
    background-color: #2D6CCA !important;
    color: white !important;
  }
  
  &:active {
    background-color: #2D6CCA !important;
    color: white !important;
  }
`;

export const TooltipContainer = styled.div`
  font-size: 13px;
  line-height: 1.6;
  max-width: 320px;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
`;

export const TooltipHeader = styled.div`
  margin-bottom: 10px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.15);
  padding-bottom: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const TooltipInfoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const TooltipLabel = styled.span`
  font-weight: 600;
  color: #a0aec0;
  min-width: 100px;
`;

export const TooltipValue = styled.span<{ $color?: string }>`
  font-weight: 700;
  color: ${(props) => props.$color || '#fff'};
  font-size: 14px;
`;

export const TooltipOptionsSection = styled.div`
  margin-top: 8px;
`;

export const TooltipOptionsTitle = styled.div`
  margin-bottom: 8px;
  font-weight: 700;
  font-size: 13px;
  color: #fff;
  letter-spacing: 0.3px;
`;

export const TooltipOptionsList = styled.div`
  font-size: 12px;
  max-height: 220px;
  overflow-y: auto;
  padding-right: 8px;
  margin-right: -8px;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.3);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.5);
  }
`;

export const TooltipOptionItem = styled.div`
  margin-bottom: 6px;
  padding: 4px 6px;
  border-radius: 4px;
  background-color: rgba(255, 255, 255, 0.05);
  display: flex;
  align-items: center;
  gap: 8px;
  transition: background-color 0.2s;
  cursor: default;

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
`;

export const TooltipSequenceBadge = styled.span`
  font-weight: 700;
  color: #4fd1c7;
  font-size: 11px;
  min-width: 32px;
  text-align: center;
  background-color: rgba(79, 209, 199, 0.2);
  padding: 2px 6px;
  border-radius: 3px;
  border: 1px solid rgba(79, 209, 199, 0.3);
`;

export const TooltipOptionText = styled.span`
  color: #e2e8f0;
  flex: 1;
  word-break: break-word;
`;

// Customizations list layout
export const DragDropListWrapper = styled.div`
  list-style: none;
  padding: 0;
  margin: 0;
`;

export const DraggableItemWrapper = styled.div<{ $isReordering: boolean }>`
  cursor: ${({ $isReordering }) => ($isReordering ? 'not-allowed' : 'grab')};
  list-style: none;
  margin-bottom: 12px;
`;

export const CustomizationInfoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const SequenceText = styled.span`
  margin-left: 8px;
  color: ${({ theme }) => (theme?.mode === 'dark' ? '#9ca3af' : '#6b7280')};
`;

export const OptionsScrollArea = styled.div`
  max-height: 320px;
  overflow-y: auto;
  padding-right: 4px;
`;

export const OptionLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
`;

export const OptionTextColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

export const OptionRight = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
  flex-shrink: 0;
  margin-left: 8px;
`;

export const OptionActionButtons = styled.div`
  display: flex;
  gap: 4px;
`;

export const OptionDescription = styled(Typography.Text)`
  font-size: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const ThumbnailWrapper = styled.div`
  position: relative;
  flex-shrink: 0;
`;

export const ThumbnailBox = styled.div<{ $isDark: boolean }>`
  width: 56px;
  height: 56px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid ${({ $isDark }) => ($isDark ? '#434343' : '#e8e8e8')};
  background: ${({ $isDark }) => ($isDark ? '#2d3748' : '#f5f5f5')};
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const ThumbnailPlaceholder = styled.div<{ $isDark: boolean }>`
  width: 56px;
  height: 56px;
  border-radius: 8px;
  background: ${({ $isDark }) => ($isDark ? '#2d3748' : '#f0f0f0')};
  border: 1px solid ${({ $isDark }) => ($isDark ? '#434343' : '#e8e8e8')};
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const ThumbnailImg = styled.img`
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  display: block;
  object-fit: contain;
`;

export const SubCard = styled.div<{ $isDark: boolean }>`
  margin-top: 12px;
  padding: 14px 16px;
  border-radius: 8px;
  border: 1px solid ${({ $isDark }) => ($isDark ? '#434343' : '#e8e8e8')};
  background: ${({ $isDark }) => ($isDark ? '#1f2937' : '#fafafa')};
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const ThumbnailEyeBadge = styled.div`
  position: absolute;
  bottom: -4px;
  right: -4px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #7c3aed;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.18);
  cursor: pointer;
`;

